import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_STRING, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_DROPDOWN } from "typeConstants/dataTypes";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { MSpinner } from "components/Shared";
import { getOnHoldReasons } from "actions/appraisalPort";
import { putOnHold, sendMessage, putOffHold } from "requests/appraisalPort";
import { putOnHold as muellerOnHold, putOffHold as muellerOffHold } from "requests/onHold";

const emptyOnHoldMessage = {
  reason: undefined,
  subject: "",
  message: ""
};

const OnHold = ({
  controlNumber,
  onHoldReasons,
  isFetchingHoldReasons,
  getOnHoldReasons,
  onSuccessCallback,
  isOnHold,
  internetUserId,
}) => {
  const [onHoldRequest, setOnHoldRequest] = useState(emptyOnHoldMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const controlMethods = useRef({});
  const mounted = useRef(true);

  useEffect(() => {
    if (!onHoldReasons.length && !isFetchingHoldReasons) {
      getOnHoldReasons();
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  if (!onHoldReasons.length || isFetchingHoldReasons || isSubmitting) {
    return <MSpinner />;
  }

  const formDefinition = [
    [{ dataType: DATA_TYPE_STRING, label: "Subject:", source: "subject" }],
    [{ dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Message:", source: "message" }]
  ];
  if (!isOnHold) {
    formDefinition.unshift(
      [{ dataType: DATA_TYPE_DROPDOWN, label: "Reason", source: "reason", dataTypeOptions: { options: onHoldReasons }, hasValidationError: validator.required }]
    );
  }

  const isValid = () => {
    if (typeof controlMethods?.current?.isFormValid === "function") {
      return controlMethods.current.isFormValid();
    }
    return false;
  };

  const handleOnChange = (updatedObject) => setOnHoldRequest({ ...updatedObject });

  const handleSend = () => {
    if (isValid()) {
      setIsSubmitting(true);
      if (!isOnHold) {
        putOnHold(controlNumber, onHoldRequest.reason, onSuccess, onError, mounted);
        muellerOnHold(controlNumber, internetUserId, null, null, null, null, mounted);
      } else {
        putOffHold(controlNumber, onSuccess, onError, mounted);
        muellerOffHold(controlNumber, internetUserId, null, null, mounted);
      }
    }
  };

  const onSuccess = () => {
    if (onHoldRequest.message) {
      sendMessage(controlNumber, onHoldRequest.subject, onHoldRequest.message, onSuccessCallback, onError, mounted);
    } else {
      if (onSuccessCallback) onSuccessCallback();
    }
  };

  const onError = () => {
    setIsSubmitting(false);
  };

  return (
    <div>
      {isOnHold && <p>
        This case is currently ON HOLD, clicking send below will send a request to take this case OFF HOLD
        </p>}
      <MGridForm
        key="appraisal-port-on-hold-form"
        data={onHoldRequest}
        onChange={handleOnChange}
        formDefinition={formDefinition}
        getControlMethods={cm => { controlMethods.current = cm; }}
        webform={true}
      />
      <Button
        key="on-hold-send-button"
        onClick={handleSend}
        disabled={!isValid()}
      >
        Send
      </Button>
    </div>
  );
};

OnHold.propTypes = {
  controlNumber: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  onHoldReasons: PropTypes.array.isRequired,
  isFetchingHoldReasons: PropTypes.bool.isRequired,
  getOnHoldReasons: PropTypes.func.isRequired,
  onSuccessCallback: PropTypes.func,  // Function to call after successfully updating on hold status
  isOnHold: PropTypes.bool.isRequired, // True if the Appraisal Port status is in On Hold (H) or On Holding Pending (OH)
  internetUserId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
};

const mapStateToProps = (state) => {
  return {
    controlNumber: state.caseDetails.controlNumber,
    onHoldReasons: state.appraisalPort.onHoldReasons,
    isFetchingHoldReasons: state.appraisalPort.isFetchingHoldReasons,
    internetUserId: state.user.profile.internetUserId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getOnHoldReasons() {
    dispatch(getOnHoldReasons());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(OnHold);
